import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { SessionUser } from '@kidzim/shared';
import { api, setAccessToken } from './api';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  registerParent: (input: { email: string; password: string; displayName: string; inviteCode?: string; consent: boolean }) => Promise<void>;
  loginParent: (input: { email: string; password: string }) => Promise<void>;
  loginKid: (input: { familyCode: string; screenName: string; pin: string }) => Promise<void>;
  /** Redeem a per-kid magic link → a room-scoped kid session. Returns the room id. */
  enterWithMagic: (token: string) => Promise<string>;
  logout: () => Promise<void>;
  /** Re-fetch the session (e.g. after a profile change) to refresh the token + user. */
  reload: () => Promise<void>;
}

interface AuthResponse {
  user: SessionUser;
  accessToken: string;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const apply = useCallback((res: AuthResponse) => {
    setAccessToken(res.accessToken);
    setUser(res.user);
  }, []);

  // Resume (or refresh) a session via the refresh cookie, then load the user.
  const loadSession = useCallback(async (): Promise<void> => {
    const refreshed = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
    if (!refreshed.ok) return;
    const data = (await refreshed.json()) as { accessToken: string };
    setAccessToken(data.accessToken);
    const me = await api<{ user: SessionUser }>('/api/auth/me');
    setUser(me.user);
  }, []);

  // On mount, try to resume a session — EXCEPT on a magic-link entry route,
  // where the link itself establishes the (room-scoped) session. Resuming there
  // would race the magic exchange and could leave us authed as whichever kid was
  // previously signed in on this browser. Let MagicEnter own the session instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (window.location.pathname.startsWith('/m/')) return;
        if (!cancelled) await loadSession();
      } catch {
        /* not signed in */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  const registerParent: AuthState['registerParent'] = useCallback(
    async (input) => apply(await api<AuthResponse>('/api/auth/register', { method: 'POST', body: JSON.stringify(input) })),
    [apply],
  );
  const loginParent: AuthState['loginParent'] = useCallback(
    async (input) => apply(await api<AuthResponse>('/api/auth/login', { method: 'POST', body: JSON.stringify(input) })),
    [apply],
  );
  const loginKid: AuthState['loginKid'] = useCallback(
    async (input) => apply(await api<AuthResponse>('/api/auth/kid/login', { method: 'POST', body: JSON.stringify(input) })),
    [apply],
  );
  const enterWithMagic: AuthState['enterWithMagic'] = useCallback(
    async (token) => {
      const res = await api<AuthResponse & { roomId: string }>('/api/auth/kid/magic', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      apply(res);
      return res.roomId;
    },
    [apply],
  );
  const logout: AuthState['logout'] = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setAccessToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, registerParent, loginParent, loginKid, enterWithMagic, logout, reload: loadSession }),
    [user, loading, registerParent, loginParent, loginKid, enterWithMagic, logout, loadSession],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
